import { useCallback, useEffect, useMemo, useState } from 'react';
import { GitBranch, Loader2, Search, StickyNote } from 'lucide-react';
import { useApi } from '../api/client';
import { useCatalogs } from '../api/useCatalogs';
import { useQuickLog } from '../components/QuickLogProvider';
import type { Catalogs, Contact, FunnelStage } from '../types/api';
import ContactEditModal, {
  fromContact,
  type ContactFormState,
} from '../components/ContactEditModal';
import NotesModal, { type NotesTarget } from '../components/NotesModal';
import Pager from '../components/Pager';
import FilterChip from '../components/FilterChip';
import SortableTh, { type SortState } from '../components/SortableTh';

// Legacy Chats grid (post-2026-05-08 redesign). Columns:
//   Name | Link sent | Status | Warmth | Booked | Appt. | Follow-up | Outcome | Notes
// Status and Warmth are contact-level; the date columns and outcome live
// on the contact's `legacy_chat` calls row, lazy-created on first edit.
// Outcome is filtered to legacy_chat-scoped values; setting outcome to
// chat_completed stamps completed_at server-side, other outcomes clear it.

interface LegacyChatRow {
  contact_id: number;
  name: string;
  platform_id: number | null;
  platform: string | null;
  platform_label: string | null;
  current_status_id: number | null;
  status: string | null;
  status_label: string | null;
  current_warmth_tier_id: number | null;
  warmth: string | null;
  warmth_label: string | null;
  // Pipeline-view fields kept on the response for backward compat /
  // future moves to a Contacts page; not surfaced on this grid.
  follow_up_due: string | null;
  notes: string | null;
  last_touched_at: string | null;
  latest_sent_at: string | null;
  // legacy_chat calls row, lazy-created on first call-field edit:
  call_id: number | null;
  call_booked_at: string | null;
  call_scheduled_at: string | null;
  call_link_sent: string | null;
  call_follow_up_due: string | null;
  call_completed_at: string | null;
  call_outcome_id: number | null;
  outcome: string | null;
  outcome_label: string | null;
  note_count: number;
}

interface ListResponse {
  rows: LegacyChatRow[];
  funnel: FunnelStage[];
  total: number;
  page: number;
  size: number;
}

const PAGE_SIZE = 50;

interface LegacyChatPatch {
  status_id?: number | null;
  warmth_tier_id?: number | null;
  call_booked_at?: string | null;
  call_scheduled_at?: string | null;
  call_link_sent?: string | null;
  call_follow_up_due?: string | null;
  call_outcome_id?: number | null;
}

const STATUS_TONE: Record<string, string> = {
  sent: 'bg-brand-mint text-brand-forest',
  replied: 'bg-brand-sky text-brand-forest',
  interested: 'bg-brand-lime text-brand-forest',
  booked: 'bg-brand-lime-700 text-white',
  discovery_call: 'bg-brand-violet text-white',
  // client_hold doubles as the funnel's Client bar — gold reads as the payoff
  // at the end of the pipeline.
  client_hold: 'bg-brand-sun text-brand-forest',
  client_won: 'bg-brand-lime-700 text-white',
  follow_up: 'bg-brand-coral text-white',
  not_now: 'bg-brand-sage text-white',
  ghosted: 'bg-brand-forest text-white',
};

const WARMTH_TONE: Record<string, string> = {
  warm: 'bg-brand-coral text-white',
  lukewarm: 'bg-brand-lime text-brand-forest',
  cold_ish: 'bg-brand-sky text-brand-forest',
};

const OUTCOME_TONE: Record<string, string> = {
  chat_completed: 'bg-brand-lime-700 text-white',
  couldnt_attend: 'bg-brand-coral text-white',
  missed_chat: 'bg-brand-sage text-white',
  chat_reschedule: 'bg-brand-sky text-brand-forest',
};

export default function LegacyChats() {
  const apiFetch = useApi();
  const { catalogs } = useCatalogs();
  const [data, setData] = useState<ListResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [sort, setSort] = useState<SortState>({ key: 'name', dir: 'asc' });
  const [editingContact, setEditingContact] = useState<ContactFormState | null>(null);
  const [notesTarget, setNotesTarget] = useState<NotesTarget | null>(null);
  const [page, setPage] = useState(1);
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Debounce the search box so type-ahead hits the server ~250ms after the
  // last keystroke rather than on every character.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  const openContact = useCallback(
    async (contactId: number) => {
      try {
        const r = await apiFetch(`/contacts/${contactId}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d: { contact: Contact } = await r.json();
        setEditingContact(fromContact(d.contact));
      } catch (e) {
        setError(String(e));
      }
    },
    [apiFetch],
  );

  const openNotes = useCallback(
    (contactId: number, name: string) =>
      setNotesTarget({ contactId, contactName: name, callType: 'legacy_chat' }),
    [],
  );

  // Each chip is an independent toggle; "All" clears the set. Filter and sort
  // changes both reset to page 1 — the current page number is meaningless
  // once the result set is reordered or resized.
  const toggleStatus = useCallback((shortName: string) => {
    setStatusFilter((cur) =>
      cur.includes(shortName)
        ? cur.filter((s) => s !== shortName)
        : [...cur, shortName],
    );
    setPage(1);
  }, []);

  const changeSort = useCallback((next: SortState) => {
    setSort(next);
    setPage(1);
  }, []);

  const refetch = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (debouncedSearch.trim()) params.set('q', debouncedSearch.trim());
      if (statusFilter.length) params.set('status', statusFilter.join(','));
      params.set('sort', sort.key);
      params.set('dir', sort.dir);
      params.set('page', String(page));
      params.set('size', String(PAGE_SIZE));
      const r = await apiFetch(`/legacy-chats?${params.toString()}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d: ListResponse = await r.json();
      setData(d);
    } catch (e) {
      setError(String(e));
    }
  }, [apiFetch, debouncedSearch, statusFilter, sort, page]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const { onLogged } = useQuickLog();
  useEffect(() => {
    return onLogged(() => {
      refetch();
    });
  }, [onLogged, refetch]);

  const patchRow = useCallback(
    async (contactId: number, patch: LegacyChatPatch) => {
      setData((d) => {
        if (!d) return d;
        return {
          ...d,
          rows: d.rows.map((row) =>
            row.contact_id === contactId ? applyOptimistic(row, patch, catalogs) : row,
          ),
        };
      });
      try {
        const r = await apiFetch(`/legacy-chats/${contactId}`, {
          method: 'PATCH',
          body: JSON.stringify(patch),
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d: { row: LegacyChatRow } = await r.json();
        setData((cur) => {
          if (!cur) return cur;
          return {
            ...cur,
            rows: cur.rows.map((row) =>
              row.contact_id === contactId ? d.row : row,
            ),
          };
        });
        if ('status_id' in patch) {
          refetch();
        }
      } catch (e) {
        setError(String(e));
        refetch();
      }
    },
    [apiFetch, catalogs, refetch],
  );

  const rows = data?.rows ?? [];

  return (
    <div className="flex h-full min-h-0 flex-col gap-6">
      <div className="flex items-center gap-3">
        <GitBranch className="h-6 w-6 text-brand-mint-600" />
        <h1 className="font-display text-3xl font-black text-brand-forest">
          Legacy Spark Chats
        </h1>
        {data && (
          <span className="text-sm text-brand-sage">
            {data.total} {data.total === 1 ? 'contact' : 'contacts'}
          </span>
        )}
      </div>

      {data && <FunnelBar funnel={data.funnel} />}

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-sage" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search by name…"
            className="w-full rounded-md border border-brand-sage/40 bg-white py-2 pl-9 pr-3 text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
          />
        </div>
        {data && (
          <Pager
            page={data.page}
            size={data.size}
            total={data.total}
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
          />
        )}
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterChip
          label="All"
          active={statusFilter.length === 0}
          onClick={() => {
            setStatusFilter([]);
            setPage(1);
          }}
        />
        {catalogs?.outreach_statuses.map((s) => (
          <FilterChip
            key={s.id}
            label={s.description}
            active={statusFilter.includes(s.short_name)}
            tone={STATUS_TONE[s.short_name]}
            onClick={() => toggleStatus(s.short_name)}
          />
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      {data == null ? (
        <div className="flex items-center gap-2 text-brand-sage">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-brand-sage/40 bg-white p-10 text-center">
          <GitBranch className="mx-auto mb-3 h-8 w-8 text-brand-sage" />
          <p className="text-brand-forest">
            {debouncedSearch || statusFilter.length
              ? 'No contacts match the current filter.'
              : 'No contacts yet. Quick-log an outreach from the Dashboard to add one.'}
          </p>
        </div>
      ) : (
        <>
          <div className="flex-1 min-h-0 overflow-auto rounded-2xl border border-brand-sage/30 bg-white shadow-sm">
            <table className="min-w-full">
              <thead className="sticky top-0 z-10 bg-brand-paper">
                <tr className="border-b border-brand-sage/20 text-left text-xs font-bold uppercase tracking-wider text-brand-sage">
                  <SortableTh
                    label="Name"
                    sortKey="name"
                    sort={sort}
                    onSort={changeSort}
                    className="sticky left-0 z-20 border-r border-brand-sage/20 bg-brand-paper"
                  />
                  <SortableTh label="Platform" sortKey="platform" sort={sort} onSort={changeSort} />
                  <SortableTh label="Link sent" sortKey="link_sent" sort={sort} onSort={changeSort} />
                  <SortableTh label="Status" sortKey="status" sort={sort} onSort={changeSort} />
                  <SortableTh label="Warmth" sortKey="warmth" sort={sort} onSort={changeSort} />
                  <SortableTh label="Booked" sortKey="booked" sort={sort} onSort={changeSort} />
                  <SortableTh label="Appt." sortKey="appt" sort={sort} onSort={changeSort} />
                  <SortableTh label="Follow-up" sortKey="follow_up" sort={sort} onSort={changeSort} />
                  <SortableTh label="Outcome" sortKey="outcome" sort={sort} onSort={changeSort} />
                  <th className="px-3 py-3">Notes</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <Row
                    key={row.contact_id}
                    row={row}
                    catalogs={catalogs}
                    onPatch={patchRow}
                    onOpenContact={openContact}
                    onOpenNotes={openNotes}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <ContactEditModal
        form={editingContact}
        onClose={() => setEditingContact(null)}
        onSaved={() => {
          setEditingContact(null);
          refetch();
        }}
        onChanged={refetch}
      />

      <NotesModal
        target={notesTarget}
        onClose={() => setNotesTarget(null)}
        onChanged={refetch}
      />
    </div>
  );
}

interface RowProps {
  row: LegacyChatRow;
  catalogs: Catalogs | null;
  onPatch: (contactId: number, patch: LegacyChatPatch) => Promise<void>;
  onOpenContact: (contactId: number) => void;
  onOpenNotes: (contactId: number, name: string) => void;
}

function Row({ row, catalogs, onPatch, onOpenContact, onOpenNotes }: RowProps) {
  // Filter outcome dropdown to legacy_chat-scoped outcomes (plus any
  // generic / null-typed ones reserved for future use).
  const legacyChatTypeId = useMemo(
    () => catalogs?.call_types.find((t) => t.short_name === 'legacy_chat')?.id ?? null,
    [catalogs],
  );
  const outcomeOptions = useMemo(
    () =>
      (catalogs?.call_outcomes ?? []).filter(
        (o) => o.call_type_id === null || o.call_type_id === legacyChatTypeId,
      ),
    [catalogs, legacyChatTypeId],
  );

  const dateCell = (
    value: string | null,
    onChange: (next: string | null) => void,
  ) => (
    <input
      type="date"
      value={value ? value.slice(0, 10) : ''}
      onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
      className="rounded-md border border-brand-sage/40 bg-white px-2 py-1 text-sm text-brand-forest focus:border-brand-lime focus:outline-none focus:ring-2 focus:ring-brand-lime/30"
    />
  );

  return (
    <tr className="border-b border-brand-sage/10 align-top last:border-0 hover:bg-brand-mint-100/30">
      <td className="sticky left-0 z-10 border-r border-brand-sage/20 bg-white px-3 py-3">
        <button
          type="button"
          onClick={() => onOpenContact(row.contact_id)}
          className="text-left font-bold text-brand-forest underline decoration-transparent underline-offset-2 transition hover:text-brand-lime-700 hover:decoration-brand-lime"
        >
          {row.name}
        </button>
      </td>

      <td className="px-3 py-3 text-sm text-brand-forest">
        {row.platform_label ?? '—'}
      </td>

      <td className="px-3 py-3">
        {dateCell(row.call_link_sent, (next) =>
          onPatch(row.contact_id, { call_link_sent: next }),
        )}
      </td>

      <td className="px-3 py-3">
        <select
          value={row.current_status_id ?? ''}
          onChange={(e) =>
            onPatch(row.contact_id, {
              status_id: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className={[
            'rounded-md border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-brand-lime/30',
            row.status && STATUS_TONE[row.status]
              ? `border-transparent ${STATUS_TONE[row.status]}`
              : 'border-brand-sage/40 bg-white text-brand-forest',
          ].join(' ')}
        >
          <option value="">—</option>
          {catalogs?.outreach_statuses.map((s) => (
            <option key={s.id} value={s.id}>
              {s.description}
            </option>
          ))}
        </select>
      </td>

      <td className="px-3 py-3">
        <select
          value={row.current_warmth_tier_id ?? ''}
          onChange={(e) =>
            onPatch(row.contact_id, {
              warmth_tier_id: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className={[
            'rounded-md border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-brand-lime/30',
            row.warmth && WARMTH_TONE[row.warmth]
              ? `border-transparent ${WARMTH_TONE[row.warmth]}`
              : 'border-brand-sage/40 bg-white text-brand-forest',
          ].join(' ')}
        >
          <option value="">—</option>
          {catalogs?.warmth_tiers.map((w) => (
            <option key={w.id} value={w.id}>
              {w.description}
            </option>
          ))}
        </select>
      </td>

      <td className="px-3 py-3">
        {dateCell(row.call_booked_at, (next) =>
          onPatch(row.contact_id, { call_booked_at: next }),
        )}
      </td>

      <td className="px-3 py-3">
        {dateCell(row.call_scheduled_at, (next) =>
          onPatch(row.contact_id, { call_scheduled_at: next }),
        )}
      </td>

      <td className="px-3 py-3">
        {dateCell(row.call_follow_up_due, (next) =>
          onPatch(row.contact_id, { call_follow_up_due: next }),
        )}
      </td>

      <td className="px-3 py-3">
        <select
          value={row.call_outcome_id ?? ''}
          onChange={(e) =>
            onPatch(row.contact_id, {
              call_outcome_id:
                e.target.value === '' ? null : Number(e.target.value),
            })
          }
          className={[
            'rounded-md border px-2 py-1 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-brand-lime/30',
            row.outcome && OUTCOME_TONE[row.outcome]
              ? `border-transparent ${OUTCOME_TONE[row.outcome]}`
              : 'border-brand-sage/40 bg-white text-brand-forest',
          ].join(' ')}
        >
          <option value="">—</option>
          {outcomeOptions.map((o) => (
            <option key={o.id} value={o.id}>
              {o.description}
            </option>
          ))}
        </select>
      </td>

      <td className="px-3 py-3">
        <button
          type="button"
          onClick={() => onOpenNotes(row.contact_id, row.name)}
          className="flex items-center gap-1.5 whitespace-nowrap rounded-md border border-brand-sage/40 bg-white px-3 py-1.5 text-sm font-bold text-brand-forest transition hover:border-brand-lime hover:text-brand-lime-700"
        >
          <StickyNote className="h-4 w-4" />
          Notes{row.note_count > 0 ? ` (${row.note_count})` : ''}
        </button>
      </td>
    </tr>
  );
}

// Stage order and labels both come from the server (common/funnel.py::
// funnel_rows) — the catalog is deliberately NOT consulted here. The Client
// bar's label differs from its status description on purpose; see FunnelStage.
function FunnelBar({ funnel }: { funnel: FunnelStage[] }) {
  return (
    <div className="flex flex-wrap gap-2 rounded-2xl border border-brand-sage/30 bg-white p-3 shadow-sm">
      {funnel.map((cell, i) => (
        <div key={cell.short_name} className="flex items-center gap-2">
          <div
            className={[
              'rounded-md px-3 py-2',
              STATUS_TONE[cell.short_name] ?? 'bg-brand-sage/15 text-brand-forest',
            ].join(' ')}
          >
            <div className="text-[10px] font-bold uppercase tracking-wider opacity-80">
              {cell.label}
            </div>
            <div className="font-display text-xl font-black leading-none">
              {cell.count}
            </div>
          </div>
          {i < funnel.length - 1 && (
            <span className="text-brand-sage/60">→</span>
          )}
        </div>
      ))}
    </div>
  );
}

function applyOptimistic(
  row: LegacyChatRow,
  patch: LegacyChatPatch,
  catalogs: Catalogs | null,
): LegacyChatRow {
  let next: LegacyChatRow = { ...row };
  if ('status_id' in patch) {
    const id = patch.status_id ?? null;
    const cat = catalogs?.outreach_statuses.find((s) => s.id === id) ?? null;
    next = {
      ...next,
      current_status_id: id,
      status: cat?.short_name ?? null,
      status_label: cat?.description ?? null,
    };
  }
  if ('warmth_tier_id' in patch) {
    const id = patch.warmth_tier_id ?? null;
    const cat = catalogs?.warmth_tiers.find((w) => w.id === id) ?? null;
    next = {
      ...next,
      current_warmth_tier_id: id,
      warmth: cat?.short_name ?? null,
      warmth_label: cat?.description ?? null,
    };
  }
  if ('call_booked_at' in patch) {
    next = { ...next, call_booked_at: patch.call_booked_at ?? null };
  }
  if ('call_scheduled_at' in patch) {
    next = { ...next, call_scheduled_at: patch.call_scheduled_at ?? null };
  }
  if ('call_link_sent' in patch) {
    next = { ...next, call_link_sent: patch.call_link_sent ?? null };
  }
  if ('call_follow_up_due' in patch) {
    next = { ...next, call_follow_up_due: patch.call_follow_up_due ?? null };
  }
  if ('call_outcome_id' in patch) {
    const id = patch.call_outcome_id ?? null;
    const cat = catalogs?.call_outcomes.find((o) => o.id === id) ?? null;
    next = {
      ...next,
      call_outcome_id: id,
      outcome: cat?.short_name ?? null,
      outcome_label: cat?.description ?? null,
    };
  }
  return next;
}